import { Area, sortTablesByDisplayOrderInArea } from '@app/domain/Area'
import { Booking } from '@app/domain/Booking'
import {
    BookingDetailsViewModel,
} from '@app/features/bookings/components/booking-graph/view-models/booking-details-view.model'
import { Table } from '@app/domain/Table'
import { Venue } from '@app/domain/Venue'

export interface CalendarBookingViewModelDelegate {
    selectBooking(booking: Booking): void
}

export class CalendarBookingViewModel extends BookingDetailsViewModel {

    private hidden: boolean = false
    private draggedAreaId: string | null = null
    private draggedTableIds: string[] = []
    private draggedStartDate: Date | null = null
    private draggedEndDate: Date | null = null
    private partySizeFocused = false

    constructor(
        booking: Booking,
        currentDateTime: Date,
        area: Area,
        private venue: Venue,
        private showBookingSource: boolean,
        private delegate?: CalendarBookingViewModelDelegate
    ) {
        super(booking, area, venue, currentDateTime)
    }

    override get tableId() {
        return this.draggedTableIds.length > 0 ? this.draggedTableIds[0] : super.tableId
    }

    get lastConsecutiveTableId() {
        const area = this.venue.areaWithTableId(this.tableId)
        if (!area) {
            return null
        }
        const tables = area.tablesSortedByDisplayOrder
        const tableIdsUsedForBooking = this.draggedTableIds.length > 0 ? this.draggedTableIds : this.booking.tableIds
        const tablesUsedForBooking = tableIdsUsedForBooking
            .map(tableId => tables.find(table => table.id === tableId))
            .filter((table): table is Table => table !== undefined)
        sortTablesByDisplayOrderInArea(tablesUsedForBooking, this.area)
        const tableIndexes = tablesUsedForBooking
            .map(table => table.id)
            .map(tableId => tables.findIndex(table => table.id === tableId))
        const firstIndex = tableIndexes[0]
        const contiguousTableIndexes = tableIndexes.filter((index, i) => index === firstIndex + i)
        return contiguousTableIndexes
            .map(index => tables[index])[contiguousTableIndexes.length - 1].id
    }

    get nonConsecutiveTableIdGroups(): string[][] {
        const area = this.venue.areaWithTableId(this.tableId)
        if (!area) {
            return []
        }
        const tables = area.tablesSortedByDisplayOrder
        const tableIdsUsedForBooking = this.draggedTableIds.length > 0 ? this.draggedTableIds : this.booking.tableIds
        const tablesUsedForBooking = tableIdsUsedForBooking
            .map(tableId => tables.find(table => table.id === tableId))
            .filter((table): table is Table => table !== undefined)
        sortTablesByDisplayOrderInArea(tablesUsedForBooking, this.area)
        const tableIndexes = tablesUsedForBooking
            .map(table => table.id)
            .map(tableId => tables.findIndex(table => table.id === tableId))
        const firstIndex = tableIndexes[0]
        const contiguousTableIndexes = tableIndexes.filter((index, i) => index === firstIndex + i)
        const nonContiguousTableIndexes = tableIndexes.filter(index => !contiguousTableIndexes.includes(index))
        const groups = []
        let currentGroup: number[] = []
        for (const index of nonContiguousTableIndexes) {
            if (currentGroup.length === 0) {
                currentGroup.push(index)
            } else if (currentGroup[currentGroup.length - 1] === index - 1) {
                currentGroup.push(index)
            } else {
                groups.push(currentGroup)
                currentGroup = [index]
            }
        }
        if (currentGroup.length > 0) {
            groups.push(currentGroup)
        }
        return groups.map(group => group.map(index => tables[index].id))
    }

    override get startDate() {
        return this.draggedStartDate ?? super.startDate
    }

    override get endDate() {
        if (this.draggedEndDate) {
            return this.draggedEndDate
        }
        if (!this.draggedStartDate) {
            return super.endDate
        }
        const date = new Date(this.draggedStartDate)
        date.setMinutes(this.draggedStartDate.getMinutes() + this.booking.duration)
        return date
    }

    override get occupiedEndDate() {
        const distance = super.endDate.getTime() - super.occupiedEndDate.getTime()
        return new Date(this.endDate.getTime() - distance)
    }

    get dateCreated() {
        return this.booking.dateCreated
    }

    override get showStatusBar() {
        return this.showBookingSource
    }

    get bookingId() {
        return this.booking.id
    }

    override get areaId() {
        return this.draggedAreaId ?? this.area.id
    }

    get isHidden() {
        return this.hidden
    }

    get depositAmountHeld(): number {
        return this.booking.deposit?.amountHeld() ?? 0
    }

    get depositAmountPending(): number {
        return this.booking.pendingDepositCharge?.amountForPartySize(this.booking.size) ?? 0
    }

    get depositCurrencyCode(): string {
        return this.booking.deposit?.currencyCode ?? 'GBP'
    }

    override get allTableNames() {
        if (this.draggedTableIds.length > 0) {
            const area = this.venue.areaWithTableId(this.draggedTableIds[0])
            return this.draggedTableIds
                .map(tableId => area?.getTableWithId(tableId)?.shortFormattedDisplayName)
                .join(', ')
        }
        return super.allTableNames
    }

    get originalTableNames() {
        return this.booking.tableIds
            .map(tableId => this.area.getTableWithId(tableId)?.shortFormattedDisplayName)
            .join(', ')
    }

    get shortName() {
        if (this.booking.isWalkIn()) {
            return 'Walk-in'
        }
        return this.booking.firstNameInitialisedName
    }

    hide() {
        this.hidden = true
    }

    show() {
        this.hidden = false
    }

    get isBeingDragged() {
        return this.draggedTableIds.length > 0
    }

    dragToTablesInArea(tables: Table[], area: Area) {
        this.draggedAreaId = area.id
        this.draggedTableIds = tables.map((table) => table.id)
    }

    dragStartToDate(date: Date) {
        this.draggedStartDate = date
    }

    dragEndToDate(date: Date) {
        this.draggedEndDate = date
    }

    drop() {
        this.draggedAreaId = null
        this.draggedTableIds = []
        this.draggedStartDate = null
        this.draggedEndDate = null
    }

    click() {
        this.delegate?.selectBooking(this.booking)
    }

    isOnTable(table: Table) {
        if (this.draggedTableIds.length > 0) {
            return this.draggedTableIds.includes(table.id)
        }
        return this.booking.occupiesAnyTable([table])
    }

    occupiesTableBetweenDates(startDate: Date, endDate: Date) {
        return this.startDate <= endDate && this.endDate >= startDate
    }

    get isPartySizeFocused() {
        return this.partySizeFocused
    }

    startPartySizeClick() {
        this.partySizeFocused = true
    }

    endPartySizeClick() {
        this.partySizeFocused = false
    }
}
